import { Checkbox, Pending } from '@cluey/cluey-components';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { Loader } from '../../../../../components/Loader';
import { getTimezone } from '../../../../../selectors/hubSelector';
import { TutorTimeSlot } from '../../../../../types/hubInterfaces';
import { DATE_FORMAT_TYPE_7, SESSION_TIME_FORMAT } from '../../../../../util/constants';

interface Props {
  timeSlotCallback(param: TutorTimeSlot): void;
  selectedDate: string;
  timeSlots: Array<TutorTimeSlot>;
  tutorName: string;
  tutorSfId: string;
  showOtherTutors: boolean;
  setShowOtherTutors: (showOtherTutors: boolean) => void;
  showHeaderSection: boolean;
  isTutorTimeSlotsLoading: boolean;
}

const TimeSlots = ({
  selectedDate,
  timeSlotCallback,
  timeSlots,
  tutorName,
  tutorSfId,
  showOtherTutors,
  setShowOtherTutors,
  showHeaderSection,
  isTutorTimeSlotsLoading,
}: Props) => {
  const timeZone: string = useSelector(getTimezone);
  const noAvailableSessionTimeSlots = (
    <>
      <p className="m-auto mb-2 text-center text-lg font-bold">No session times available</p>
      <p className="m-auto text-center text-base">Try a different date to see other options</p>
    </>
  );

  return (
    <section>
      {isEmpty(timeSlots) && !showHeaderSection ? (
        noAvailableSessionTimeSlots
      ) : (
        <>
          <p className="text-[20px] font-bold">Select session time</p>
          <p className="mt-3 text-lg text-grey-5">
            The following times are available on {moment(selectedDate).format(DATE_FORMAT_TYPE_7)}
          </p>
          <div className="my-5 flex items-center gap-x-3">
            <div className="shrink-0">
              <Checkbox
                value={!showOtherTutors}
                id="showOtherTutors"
                onChange={() => {
                  setShowOtherTutors(!showOtherTutors);
                }}
              />
            </div>
            <div className="flex flex-col gap-y-1">
              <label className="cursor-pointer text-base" htmlFor="showOtherTutors">
                {`${tutorName}'s availability only`}
              </label>
            </div>
          </div>
          <div className="mt-4">
            <Pending loading={isTutorTimeSlotsLoading} loader={<Loader />}>
              {!isEmpty(timeSlots) ? (
                <>
                  {timeSlots.map((timeSlot) => (
                    <button
                      key={timeSlot.start}
                      className="mb-3 flex w-full cursor-pointer items-center justify-between rounded-lg border-2 border-slate-1 bg-slate-1 px-7 py-6 transition-colors hover:border-slate-2"
                      type="button"
                      tabIndex={-1}
                      onClick={() => {
                        timeSlotCallback(timeSlot);
                      }}
                    >
                      <div className="flex w-full flex-row items-center justify-between">
                        <span className="text-lg font-bold text-primary lg:text-2xl">
                          {moment.utc(timeSlot.start).tz(timeZone).format(SESSION_TIME_FORMAT)}
                        </span>
                        <span
                          className={classNames(
                            'text-sm text-grey-5',
                            tutorSfId === timeSlot.tutorsAvailable[0].tutorId && 'font-bold'
                          )}
                        >
                          {tutorSfId === timeSlot.tutorsAvailable[0].tutorId
                            ? tutorName
                            : timeSlot.tutorsAvailable[0].tutorName}{' '}
                          available
                        </span>
                      </div>
                    </button>
                  ))}
                </>
              ) : (
                noAvailableSessionTimeSlots
              )}
            </Pending>
          </div>
        </>
      )}
    </section>
  );
};

export { TimeSlots };
